import React from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { Link } from 'react-router-dom';
import { ArrowRight, Plane, Train, Briefcase, Calendar, Clock, Shield, Car, Star, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import { SERVICE_CATALOG, servicePath } from '../navigation/serviceCatalog';

const SERVICE_KEYS = [
  { icon: Plane, key: 'airport' },
  { icon: Train, key: 'train' },
  { icon: Briefcase, key: 'business' },
  { icon: Calendar, key: 'events' },
];

const ADVANTAGE_KEYS = [
  { icon: Clock, key: 'punctuality' },
  { icon: Shield, key: 'security' },
  { icon: Car, key: 'comfort' },
  { icon: Star, key: 'service' },
];

const ServicesPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#f8f8f8]" data-testid="services-page">
          <Seo {...seo.services} />
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-white border-b border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
              {t('servicesPage.badge')}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-black tracking-tight mb-6">
              {t('servicesPage.title1')}<br />
              <span className="text-neutral-400">{t('servicesPage.title2')}</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-600 max-w-2xl leading-relaxed">
              {t('servicesPage.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-[#f8f8f8]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {SERVICE_KEYS.map((service, i) => (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-white border border-neutral-100 rounded-2xl p-8 md:p-10 hover:border-neutral-200 hover:shadow-lg transition-all duration-300"
                data-testid={`service-detail-${i}`}
              >
                <div className="w-14 h-14 bg-neutral-100 group-hover:bg-black rounded-xl flex items-center justify-center mb-6 transition-colors duration-300">
                  <service.icon className="w-7 h-7 text-neutral-700 group-hover:text-white transition-colors duration-300" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-black mb-4">
                  {t(`servicesPage.services.${service.key}.title`)}
                </h2>
                <p className="text-neutral-600 mb-6 leading-relaxed">
                  {t(`servicesPage.services.${service.key}.description`)}
                </p>
                <ul className="space-y-3">
                  {[1, 2, 3, 4].map(j => (
                    <li key={j} className="flex items-center gap-3 text-neutral-700">
                      <div className="w-5 h-5 bg-neutral-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-neutral-600" />
                      </div>
                      {t(`servicesPage.services.${service.key}.feature${j}`)}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-black tracking-tight mb-4">
              {t('servicesPage.advantages.title')}
            </h2>
            <p className="text-neutral-500 max-w-2xl mx-auto text-lg">
              {t('servicesPage.advantages.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {ADVANTAGE_KEYS.map((adv, i) => (
              <motion.div
                key={adv.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group text-center p-8 bg-neutral-50 border border-neutral-100 rounded-2xl hover:bg-white hover:border-neutral-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:scale-105 transition-transform duration-300">
                  <adv.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-black mb-2">
                  {t(`servicesPage.advantages.${adv.key}.title`)}
                </h3>
                <p className="text-neutral-500 text-sm">{t(`servicesPage.advantages.${adv.key}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WP-3.3 — Service hub → per-service page links (internal linking; ratified SVC-2 catalogue). */}
      <section className="py-16 md:py-20 bg-[#f8f8f8] border-t border-neutral-100" data-testid="service-links">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-black tracking-tight mb-8">
            Nos prestations
          </h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICE_CATALOG.map((s) => (
              <li key={s.slug}>
                <Link
                  to={servicePath(s.slug)}
                  className="flex items-center justify-between rounded-xl border border-neutral-200 bg-white px-5 py-4 text-neutral-800 hover:border-neutral-400 hover:shadow-sm transition"
                  data-testid={`service-link-${s.slug}`}
                >
                  <span className="font-medium">{s.name}</span>
                  <ArrowRight className="w-4 h-4 text-neutral-400" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-6">
              {t('servicesPage.cta.title')}
            </h2>
            <p className="text-neutral-400 max-w-xl mx-auto mb-10 text-lg">
              {t('servicesPage.cta.subtitle')}
            </p>
            <Link to="/reserver">
              <Button
                className="bg-white text-black hover:bg-neutral-100 px-10 py-6 text-base font-semibold rounded-xl shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                data-testid="services-book-btn"
              >
                {t('servicesPage.cta.button')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
